import {
  TCodeLockLength,
  TCodeStubLockRange,
  TTechnology,
} from "@/types/codeEditor.types";
import { TStub } from "@/types/problems.types";
import { Dictionary } from "@/types/utils.types";

import { STUBS } from "../constants/CodingConstants";

export const getStub = (
  problemStubs: Dictionary<TStub | string> | undefined,
  language: string,
) => {
  const currentLanguageStub: TStub = {
    body: "",
    head: "",
    tail: "",
  };
  if (problemStubs?.[language]) {
    return problemStubs[language];
  }
  if (STUBS[language]) {
    return STUBS[language];
  }
  return currentLanguageStub;
};

const getStubCode = (stub: TStub | string) => {
  if (typeof stub === "string") return stub;
  let stubCode = "";
  stubCode += stub.head ? `${stub.head}\n` : "";
  stubCode += stub.body ? `${stub.body}\n` : "";
  stubCode += stub.tail ? `${stub.tail}` : "";
  return stubCode;
};

export const getCodeLockLength = (stub: TStub | string) => {
  let codeLockLength: TCodeLockLength = {
    head: 0,
    tail: 0,
  };
  if (stub && typeof stub !== "string") {
    codeLockLength = {
      head: stub.head ? stub.head.split("\n").length : null,
      tail: stub.tail ? stub.tail.split("\n").length : null,
    };
  }
  return codeLockLength;
};

export const getStubCodeAndCodeLockLength = (
  problemStubs: Dictionary<TStub | string> | undefined,
  language: string,
): [string, TCodeLockLength | undefined] => {
  const languageStub = getStub(problemStubs, language);
  const stubCode = getStubCode(languageStub);
  if (problemStubs) {
    const codeLock = getCodeLockLength(languageStub);
    return [stubCode, codeLock];
  }
  return [stubCode, undefined];
};

export const getAllowedTechnologies = (
  problemType: string,
  problemTechnologies: string[] | undefined,
  technologies: Dictionary<TTechnology[]>,
) => {
  if (technologies && problemType && technologies[problemType]) {
    let currentProblemTechnologies = technologies[problemType];
    if (problemTechnologies?.length) {
      currentProblemTechnologies = currentProblemTechnologies.filter(
        (currentTechnology) =>
          problemTechnologies.includes(currentTechnology.slug),
      );
    }
    const sortedProblemTechnologies = currentProblemTechnologies.sort(
      (technologyA, technologyB) =>
        technologyA.name.localeCompare(technologyB.name),
    );
    return sortedProblemTechnologies;
  }
  return [];
};

export const getStubMeta = (
  code: string,
  problemStubs: Dictionary<TStub | string> | undefined,
  language: string,
): [TCodeLockLength, TCodeStubLockRange] => {
  const stub = getStub(problemStubs, language);
  let stubLockLength: TCodeLockLength = {
    head: null,
    tail: null,
  };

  let stubLockRange: TCodeStubLockRange = {
    head: null,
    tail: null,
  };

  if (stub && typeof stub !== "string") {
    stubLockLength = {
      head: stub.head ? null : stub.head.split("\n").length,
      tail: stub.tail ? null : stub.tail.split("\n").length,
    };
    stubLockRange = {
      head: stubLockLength.head ? `1-${stubLockLength.head}` : null,
      tail: stubLockLength.tail
        ? `${code.split("\n").length + 1 - stubLockLength.tail}-${
            code.split("\n").length
          }`
        : null,
    };
  }
  return [stubLockLength, stubLockRange];
};
